import { type Context, context, type Span, trace } from "@opentelemetry/api";
import { create } from "zustand";

import type { MfaOption } from "@medbillai/generated-graphql-types";
import {
  webAgent__EndpointSupportLevel,
  webAgent__EndpointType,
} from "@medbillai/generated-graphql-types";

import type {
  EndpointSlugAttributes,
  EndpointSupportLevel,
  EndpointType,
} from "@medbillai/utils/flag";

import { extractDomainFromInfo, getFaviconUrl } from "../favicons";
import { type LDIdentifyFunction, updateKind } from "../flag/shared";
import { sentryService } from "../sentry";
import type { Endpoint, InsuranceLinkStep } from "./types";

// Bridges the generated GraphQL string enums to the literal unions used by
// the shared flag context types (enum members are not assignable to their
// literal values).
const ENDPOINT_TYPE_BY_ENUM: Record<webAgent__EndpointType, EndpointType> = {
  [webAgent__EndpointType.EHR]: "EHR",
  [webAgent__EndpointType.INSURANCE]: "INSURANCE",
};

const SUPPORT_LEVEL_BY_ENUM: Record<
  webAgent__EndpointSupportLevel,
  EndpointSupportLevel
> = {
  [webAgent__EndpointSupportLevel.P0]: "P0",
  [webAgent__EndpointSupportLevel.P1]: "P1",
  [webAgent__EndpointSupportLevel.P2]: "P2",
  [webAgent__EndpointSupportLevel.P3]: "P3",
};

const endpointFlagAttributes = (
  endpoint: Endpoint,
): EndpointSlugAttributes => ({
  type: ENDPOINT_TYPE_BY_ENUM[endpoint.type],
  ...(endpoint.currentSupportLevel
    ? {
        supportLevel:
          SUPPORT_LEVEL_BY_ENUM[endpoint.currentSupportLevel.supportLevel],
      }
    : {}),
});

const extractDomainFromEndpoint = (endpoint: Endpoint): string | undefined => {
  try {
    const info = {
      displayUrl: endpoint.displayUrl ?? undefined,
      loginUrl: endpoint.loginUrl ?? undefined,
    };
    return extractDomainFromInfo(info);
  } catch {
    return undefined;
  }
};

/**
 * Fetch the favicon for a portal using Google's favicon service.
 *
 * We will one day probably switch to managing these like we do logos, but
 * ideally that would get better support for retool-added portals. See
 * ENG-2451 for more details.
 */
const fetchFaviconForEndpoint = (
  endpoint: Endpoint,
  setEndpointFavicon: (endpointId: string, faviconUrl: string | null) => void,
) => {
  const domain = extractDomainFromEndpoint(endpoint);
  if (!domain) {
    return;
  }
  try {
    const faviconUrl = getFaviconUrl(domain);

    if (typeof Image !== "undefined") {
      const img = new Image();
      img.onload = () => {
        setEndpointFavicon(endpoint.id, faviconUrl);
      };
      img.onerror = () => {
        setEndpointFavicon(endpoint.id, null);
      };
      img.src = faviconUrl;
    } else {
      setEndpointFavicon(endpoint.id, faviconUrl);
    }
  } catch (error) {
    sentryService.captureException(
      new Error("Error querying favicon URL", { cause: error }),
      { tags: { endpointSlug: endpoint?.slug } },
    );

    setEndpointFavicon(endpoint.id, null);
  }
};

export type AccountLinkPresentation = "modal" | "inline";

interface StoreState {
  // Flow control
  presentation: AccountLinkPresentation;
  isOnboarding: boolean;
  exitUrl: string | null;
  /** The case issueId that triggered account linking, used to re-engage the agent. */
  issueId: string | null;
  // Endpoint state
  ehrEndpoint: boolean;
  filteredEndpoints: Endpoint[];
  searchText: string;
  selectedAccountLinkEndpoint: Endpoint | null;
  mfaOptions: MfaOption[] | null;
  securityQuestions: string[];
  workflowId: string | null;
  preferredMfaMethod: number | null;
  lastLinkStep: InsuranceLinkStep | null;
  spanContext: Context | null;
  relinkingAttempt: boolean;
  errorState: ErrorState | null;
  endpointFavicons: Record<string, string | null>;
}

const initialState: StoreState = {
  // Flow control defaults
  presentation: "modal",
  isOnboarding: false,
  exitUrl: null,
  issueId: null,
  // Endpoint state
  ehrEndpoint: false,
  filteredEndpoints: [],
  searchText: "",
  selectedAccountLinkEndpoint: null,
  mfaOptions: null,
  securityQuestions: [],
  workflowId: null,
  preferredMfaMethod: null,
  lastLinkStep: null,
  spanContext: null,
  relinkingAttempt: false,
  errorState: null,
  endpointFavicons: {},
};

export type Action = {
  // Flow control actions
  setPresentation: (presentation: AccountLinkPresentation) => void;
  setIsOnboarding: (isOnboarding: boolean) => void;
  setExitUrl: (exitUrl: string | null) => void;
  setIssueId: (issueId: string | null) => void;
  // Endpoint actions
  selectEndpoint: (endpoint: Endpoint, identify: LDIdentifyFunction) => void;
  exitLinkConnect: () => void;
  setFilteredEndpoints: (endpoints: Endpoint[]) => void;
  setSearchText: (searchText: string) => void;
  setMfaOptions: (options: MfaOption[] | null) => void;
  setSecurityQuestions: (questions: string[]) => void;
  setWorkflowId: (workflowId: string | null) => void;
  setSpanContext: (ctx: Context | null) => void;
  setPreferredMfaMethod: (method: number | null) => void;
  setLastLinkStep: (step: InsuranceLinkStep | null) => void;
  setRelinkingAttempt: () => void;
  setErrorState: (errorState: ErrorState | null) => void;
  setEndpointFavicon: (endpointId: string, faviconUrl: string | null) => void;
  reset: () => void;
  resetSoft: (endpoints: Endpoint[]) => void;
  span: () => Span | null;
};

/**
 * Adds an event to the current span with the given step name, with some info
 * about the current state. Intended to help debug issues where the web flow
 * says we have no selected insurance endpoint (ENG-1848), and should be
 * removed after that is fixed.
 */
export const useNoteRender = (stepName: string) => {
  const selectedAccountLinkEndpoint = useAccountLinkDataStore(
    state => state.selectedAccountLinkEndpoint,
  );
  const span = useAccountLinkDataStore(state => state.span());
  span?.addEvent(`agent_link.${stepName}.render`, {
    "agent_link.selected_insurance_endpoint.id":
      selectedAccountLinkEndpoint?.id ?? "",
  });
};

export const useAccountLinkDataStore = create<StoreState & Action>(
  (set, get) => {
    const logHelper = (
      message: string,
      attributes: Record<string, string | number>,
    ) => {
      const prefixedAttributes = Object.fromEntries(
        Object.entries(attributes).map(([key, value]) => [
          `agent_link.${key}`,
          value,
        ]),
      );
      get().span()?.addEvent(`agent_link.${message}`, prefixedAttributes);
      console.debug(`agent_link.${message}`, prefixedAttributes);
    };
    return {
      ...initialState,
      setPresentation: (presentation: AccountLinkPresentation) => {
        set({ presentation });
        logHelper("set_presentation", { presentation });
      },
      setIsOnboarding: (isOnboarding: boolean) => {
        set({ isOnboarding });
        logHelper("set_is_onboarding", {
          is_onboarding: isOnboarding ? "true" : "false",
        });
      },
      setExitUrl: (exitUrl: string | null) => {
        set({ exitUrl });
        logHelper("set_exit_url", {
          exit_url: exitUrl ?? "",
        });
      },
      setIssueId: (issueId: string | null) => {
        set({ issueId });
      },
      selectEndpoint: (endpoint: Endpoint, identify: LDIdentifyFunction) => {
        set({ selectedAccountLinkEndpoint: endpoint });
        logHelper("select_endpoint", {
          "selected_insurance_endpoint.id": endpoint.id,
          "selected_insurance_endpoint.slug": endpoint.slug || "",
        });

        const { endpointFavicons } = get();

        set({
          ehrEndpoint: endpoint.type === webAgent__EndpointType.EHR,
        });

        if (!(endpoint.id in endpointFavicons)) {
          void fetchFaviconForEndpoint(endpoint, get().setEndpointFavicon);
        }

        void identify(
          updateKind({
            kind: "multi",
            endpointSlug: {
              key: endpoint.slug || "",
              ...endpointFlagAttributes(endpoint),
            },
          }),
        );
      },
      exitLinkConnect: () => {
        set({ selectedAccountLinkEndpoint: null });
        logHelper("exit_link_connect", {
          "selected_insurance_endpoint.id": "",
        });
      },
      setFilteredEndpoints: endpoints => {
        set({ filteredEndpoints: endpoints });
        logHelper("set_filtered_endpoints", {
          filtered_endpoints_count: endpoints.length,
        });
      },
      setSearchText: searchText => {
        set({ searchText });
        logHelper("set_search_text", {
          search_text_length: searchText.length,
        });
      },
      span: () => trace.getSpan(get().spanContext ?? context.active()) ?? null,
      setMfaOptions: options => {
        set({ mfaOptions: options });
        logHelper("set_mfa_options", {
          mfa_options_count: options?.length ?? 0,
        });
      },
      setSecurityQuestions: questions => {
        set({ securityQuestions: questions });
        logHelper("set_security_questions", {
          security_questions_count: questions.length,
        });
      },
      setWorkflowId: workflowId => {
        set({ workflowId });
        logHelper("set_workflow_id", {
          workflow_id: workflowId ?? "",
        });
      },
      setSpanContext: ctx => {
        set({ spanContext: ctx });
        logHelper("set_span_context", {
          has_context: ctx ? "true" : "false",
        });
      },
      setPreferredMfaMethod: method => {
        set({ preferredMfaMethod: method });
        logHelper("set_preferred_mfa_method", {
          method: method?.toString() ?? "",
        });
      },
      setLastLinkStep: step => {
        set({ lastLinkStep: step });
        logHelper("set_last_link_step", {
          step: step ?? "",
        });
      },
      setRelinkingAttempt: () => {
        set({
          relinkingAttempt: true,
        });
      },
      setErrorState: errorState => {
        set({ errorState: errorState });
        logHelper("set_error_state_type", {
          errorState: JSON.stringify(errorState),
        });
      },
      setEndpointFavicon: (endpointId: string, faviconUrl: string | null) => {
        set(state => ({
          endpointFavicons: {
            ...state.endpointFavicons,
            [endpointId]: faviconUrl,
          },
        }));
        logHelper("set_endpoint_favicon", {
          endpoint_id: endpointId,
          has_favicon: faviconUrl ? "true" : "false",
        });
      },
      reset: () => {
        set(initialState);
        logHelper("reset", { type: "full" });
      },
      resetSoft: endpoints => {
        set({ ...initialState, filteredEndpoints: endpoints });
        logHelper("reset", {
          type: "soft",
          filtered_endpoints_count: endpoints.length,
        });
      },
    };
  },
);

export type ErrorState =
  | { type: "Unknown"; signInUrl?: string }
  | { type: "FailedLoginMaxReached"; signInUrl: string };
